// ══════════════════════════════════════════════════════
// Strategy Lab — Deploy Tab
// ══════════════════════════════════════════════════════
// Unified view over a strategy's lifecycle: all experiments, all runs,
// parameter-level synthesis, promote workflow, history/rollback.
// ══════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from "react";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Pill, Button } from "../../components/ui";
import { toast } from "../../components/shared";
import { API_BASE } from "../../config";

const POLL_INTERVAL_MS = 15000;

// ─── Helpers ───────────────────────────────────────────

const fmtUsd = (v) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  return (n >= 0 ? "+$" : "-$") + Math.abs(n).toLocaleString("de-DE", { maximumFractionDigits: 0 });
};
const fmtPct = (v) => v == null || isNaN(v) ? "—" : (Number(v) * (Number(v) > 1 ? 1 : 100)).toFixed(1) + "%";
const fmtNum = (v, digits = 2) => v == null || isNaN(v) ? "—" : Number(v).toFixed(digits);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

// Verdict badge colors
function verdictStyle(verdict) {
  const v = (verdict || "").toUpperCase();
  if (v.includes("ROBUST") || v.includes("STARK")) return { bg: theme.greenLight2, color: theme.greenText, label: verdict };
  if (v.includes("SOLIDE") || v.includes("SOLID")) return { bg: "#fef9c3", color: "#854d0e", label: verdict };
  if (v.includes("FRAGIL") || v.includes("FRAGILE")) return { bg: "#fed7aa", color: "#9a3412", label: verdict };
  if (v.includes("SCHWACH") || v.includes("WEAK")) return { bg: theme.redLight, color: theme.redText, label: verdict };
  return { bg: theme.grayLight, color: theme.text3, label: verdict || "—" };
}


export default function DeployTab({ stratFile, onNavigate }) {
  const [strategies, setStrategies] = useState([]);
  const [selectedFilename, setSelectedFilename] = useState(stratFile || null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(null);
  const [selectedExp, setSelectedExp] = useState(null);      // selected experiment filename
  const [diff, setDiff] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [promoteNote, setPromoteNote] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [versionBump, setVersionBump] = useState("auto");
  const [forcePromote, setForcePromote] = useState(false);

  // ─── API loaders ──────────────────────────────

  const loadStrategies = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/deploy/strategies?_t=${Date.now()}`);
      if (!r.ok) throw new Error("Failed to load strategies");
      const d = await r.json();
      setStrategies(d.strategies || []);
      if (!selectedFilename && d.strategies?.length > 0) {
        setSelectedFilename(d.strategies[0].filename);
      }
    } catch (e) { setError(String(e)); }
  }, [selectedFilename]);

  const loadOverview = useCallback(async (silent = false) => {
    if (!selectedFilename) return;
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/deploy/strategy/${encodeURIComponent(selectedFilename)}/overview?_t=${Date.now()}`);
      if (!r.ok) throw new Error("Failed to load overview");
      const d = await r.json();
      setOverview(d);
      setError(null);
    } catch (e) {
      setError(String(e));
      setOverview(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedFilename]);

  const loadHistory = useCallback(async () => {
    if (!selectedFilename) return;
    try {
      const r = await fetch(`${API_BASE}/deploy/strategy/${encodeURIComponent(selectedFilename)}/history?_t=${Date.now()}`);
      if (r.ok) setHistory(await r.json());
    } catch {}
  }, [selectedFilename]);

  const loadExperimentDetail = useCallback(async (expFilename) => {
    setSelectedExp(expFilename);
    setDiff(null);
    setPreflight(null);
    if (!expFilename) return;
    try {
      const [diffR, preR] = await Promise.all([
        fetch(`${API_BASE}/deploy/experiment/${encodeURIComponent(expFilename)}/diff?_t=${Date.now()}`),
        fetch(`${API_BASE}/deploy/strategy/${encodeURIComponent(selectedFilename)}/preflight/${encodeURIComponent(expFilename)}?_t=${Date.now()}`),
      ]);
      if (diffR.ok) setDiff(await diffR.json());
      if (preR.ok) setPreflight(await preR.json());
    } catch (e) { /* noop */ }
  }, [selectedFilename]);

  // ─── Effects ──────────────────────────────────

  useEffect(() => { loadStrategies(); }, [loadStrategies]);
  useEffect(() => { loadOverview(); loadHistory(); setSelectedExp(null); }, [loadOverview, loadHistory]);

  // Auto-poll
  useEffect(() => {
    if (!selectedFilename) return;
    const id = setInterval(() => loadOverview(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [selectedFilename, loadOverview]);

  // ─── Actions ──────────────────────────────────

  async function doPromote() {
    if (!selectedExp) return;
    setPromoting(true);
    try {
      const r = await fetch(`${API_BASE}/deploy/strategy/${encodeURIComponent(selectedFilename)}/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          experiment_filename: selectedExp,
          note: promoteNote || null,
          force: forcePromote,
          version_bump: versionBump,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        toast(`✅ Promoted zu v${d.new_version} — Backup: ${d.backup}`, "success");
        setPromoteModalOpen(false);
        setPromoteNote("");
        setForcePromote(false);
        setSelectedExp(null);
        loadOverview();
        loadHistory();
      } else {
        toast(d.detail || "Promote fehlgeschlagen", "error");
      }
    } catch (e) { toast(String(e), "error"); }
    finally { setPromoting(false); }
  }

  async function doRollback(backupFilename) {
    if (!confirm(`Wirklich auf Backup "${backupFilename}" zurückrollen?\n\nDer aktuelle Stand wird zuerst gesichert.`)) return;
    try {
      const r = await fetch(`${API_BASE}/deploy/strategy/${encodeURIComponent(selectedFilename)}/rollback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ backup_filename: backupFilename }),
      });
      const d = await r.json();
      if (r.ok) {
        toast(`✅ Rollback zu v${d.new_version} — Pre-Rollback-Backup: ${d.pre_rollback_backup}`, "success");
        loadOverview();
        loadHistory();
      } else {
        toast(d.detail || "Rollback fehlgeschlagen", "error");
      }
    } catch (e) { toast(String(e), "error"); }
  }

  // ─── Render ──────────────────────────────────

  const currentStrategy = strategies.find(s => s.filename === selectedFilename);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* HEADER — Strategy Selector */}
      <div style={{
        padding: "14px 18px", borderRadius: 10,
        background: theme.white, border: `1px solid ${theme.border}`,
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 18 }}>🚀</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>Deploy</div>
          <div style={{ fontSize: 11, color: theme.text3 }}>
            Zentrales Kontroll-Center: Experimente, Synthese, Promote-Workflow, Versions-Historie
          </div>
        </div>
        <div style={{ flex: 1 }}/>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 11, color: theme.text3, fontWeight: 600 }}>Strategie:</label>
          <select value={selectedFilename || ""}
                  onChange={e => setSelectedFilename(e.target.value)}
                  style={{
                    padding: "8px 12px", fontSize: 12, fontFamily: fonts.mono,
                    border: `1px solid ${theme.border}`, borderRadius: 6,
                    background: theme.white, color: theme.text, minWidth: 260,
                  }}>
            {strategies.map(s => (
              <option key={s.filename} value={s.filename}>
                {s.instrument} · {s.filename} · v{s.version} · {s.status}
              </option>
            ))}
          </select>
          <Button onClick={() => loadOverview()} style={{ fontSize: 11, padding: "6px 10px" }}>
            ↻ Refresh
          </Button>
        </div>
      </div>

      {loading && !overview && (
        <div style={{ padding: 40, textAlign: "center", color: theme.text3 }}>Lade Overview…</div>
      )}
      {error && (
        <div style={{ padding: 12, background: theme.redLight, color: theme.redText, borderRadius: 8, fontSize: 12 }}>
          {error}
        </div>
      )}

      {overview && (
        <>
          {/* VERDICT PANEL — top-level recommendation */}
          <VerdictPanel verdict={overview.verdict} strategy={currentStrategy}/>

          {/* MAIN GRID — 2 columns */}
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16, alignItems: "start" }}>

            {/* LEFT: Synthesis */}
            <SynthesisPanel synthesis={overview.synthesis} verdict={overview.verdict}/>

            {/* RIGHT: History / Backups */}
            <HistoryPanel history={history} currentVersion={overview.version} onRollback={doRollback}/>
          </div>

          {/* EXPERIMENTS TABLE */}
          <ExperimentsTable
            experiments={overview.experiments || []}
            baseRuns={overview.base_runs}
            selectedExp={selectedExp}
            onSelectExp={loadExperimentDetail}
          />

          {/* EXPERIMENT DETAIL PANEL */}
          {selectedExp && (
            <ExperimentDetailPanel
              expFilename={selectedExp}
              diff={diff}
              preflight={preflight}
              onClose={() => { setSelectedExp(null); setDiff(null); setPreflight(null); }}
              onPromote={() => setPromoteModalOpen(true)}
            />
          )}
        </>
      )}

      {/* PROMOTE MODAL */}
      {promoteModalOpen && (
        <PromoteModal
          expFilename={selectedExp}
          preflight={preflight}
          versionBump={versionBump}
          setVersionBump={setVersionBump}
          note={promoteNote}
          setNote={setPromoteNote}
          force={forcePromote}
          setForce={setForcePromote}
          promoting={promoting}
          onCancel={() => setPromoteModalOpen(false)}
          onConfirm={doPromote}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════
// Verdict Panel — top-level recommendation
// ══════════════════════════════════════════════════════
function VerdictPanel({ verdict, strategy }) {
  if (!verdict) return null;
  const statusColor = verdict.overall_status.includes("READY") ? theme.greenText
                    : verdict.overall_status.includes("CAUTION") ? "#854d0e"
                    : theme.redText;
  const statusBg = verdict.overall_status.includes("READY") ? theme.greenLight2
                 : verdict.overall_status.includes("CAUTION") ? "#fef9c3"
                 : theme.redLight;

  return (
    <div style={{
      padding: 18, borderRadius: 10,
      background: statusBg, border: `2px solid ${statusColor}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: statusColor }}>
          {verdict.overall_status}
        </div>
        <div style={{ fontSize: 13, color: theme.text, fontWeight: 600 }}>{verdict.headline}</div>
        <div style={{ flex: 1 }}/>
        <div style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono }}>
          {verdict.n_opt_runs} opt · {verdict.n_wf_runs} wf · {verdict.n_stable_params}/{verdict.n_total_params} params stable
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {verdict.lines.map((line, i) => (
          <div key={i} style={{ fontSize: 12, color: theme.text2 }}>{line}</div>
        ))}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════
// Synthesis Panel — parameter-level aggregation
// ══════════════════════════════════════════════════════
function SynthesisPanel({ synthesis, verdict }) {
  const [expandedParam, setExpandedParam] = useState(null);

  const params = useMemo(() => {
    if (!synthesis || typeof synthesis !== "object") return [];
    return Object.entries(synthesis)
      .map(([name, data]) => ({ name, ...(data || {}) }))
      .sort((a, b) => {
        const order = { "drift": 0, "moderate": 1, "stable": 2, "single": 3 };
        return (order[a.stability] ?? 99) - (order[b.stability] ?? 99);
      });
  }, [synthesis]);

  if (params.length === 0) {
    return (
      <Card>
        <CardHeader title="📊 Parameter-Synthese" sub="Aggregation über alle Läufe"/>
        <div style={{ padding: 20, fontSize: 12, color: theme.text3, textAlign: "center" }}>
          Keine Optimizer-Runs vorhanden — Synthese nicht möglich.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="📊 Parameter-Synthese"
                  sub={`${params.length} Parameter über ${verdict?.n_opt_runs || 0} Opt-Läufe`}/>
      <div style={{ padding: "4px 0" }}>
        {params.map(p => {
          const stabColor = p.stability === "stable" ? theme.greenText
                          : p.stability === "drift" ? theme.redText
                          : p.stability === "moderate" ? "#854d0e" : theme.text3;
          const stabBg = p.stability === "stable" ? theme.greenLight2
                       : p.stability === "drift" ? theme.redLight
                       : p.stability === "moderate" ? "#fef9c3" : theme.grayLight;
          const stabIcon = p.stability === "stable" ? "✅"
                         : p.stability === "drift" ? "🔴"
                         : p.stability === "moderate" ? "🟡" : "📊";
          const baseMatches = p.base_value != null && String(p.base_value) === String(p.best_value);
          const expanded = expandedParam === p.name;
          return (
            <div key={p.name} style={{ borderBottom: `1px solid ${theme.divider}` }}>
              <div onClick={() => setExpandedParam(expanded ? null : p.name)}
                   style={{
                     padding: "10px 16px", cursor: "pointer",
                     display: "flex", alignItems: "center", gap: 10,
                     userSelect: "none",
                   }}>
                <span style={{ fontSize: 14 }}>{stabIcon}</span>
                <span style={{
                  fontSize: 12, fontWeight: 700, fontFamily: fonts.mono, color: theme.text,
                  minWidth: 180,
                }}>{p.name}</span>
                <span style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 10,
                  background: stabBg, color: stabColor, fontWeight: 700,
                }}>
                  {p.stability.toUpperCase()}
                </span>
                <div style={{ flex: 1 }}/>
                <span style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono }}>
                  best: <strong style={{ color: theme.text }}>{String(p.best_value)}</strong> ({p.dominant_share_pct}%)
                </span>
                {p.base_value != null && (
                  <span style={{
                    fontSize: 10, padding: "1px 6px", borderRadius: 4,
                    background: baseMatches ? theme.greenLight2 : "#fff7ed",
                    color: baseMatches ? theme.greenText : "#9a3412",
                    fontFamily: fonts.mono,
                  }}>
                    base: {String(p.base_value)}
                  </span>
                )}
                <span style={{ fontSize: 11, color: theme.text4, fontFamily: fonts.mono }}>
                  {expanded ? "▾" : "▸"}
                </span>
              </div>
              {expanded && (
                <div style={{ padding: "8px 16px 14px 44px", background: theme.tint, fontSize: 11 }}>
                  <div style={{ marginBottom: 6, color: theme.text2, fontStyle: "italic" }}>
                    {p.recommendation}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    <span style={{ color: theme.text3, marginRight: 8, fontWeight: 600 }}>
                      Getestete Werte ({p.tested_values.length}):
                    </span>
                    {Object.entries(p.frequency || {}).map(([val, count]) => (
                      <span key={val} style={{
                        fontFamily: fonts.mono, fontSize: 10,
                        padding: "2px 7px", borderRadius: 10,
                        background: String(val) === String(p.best_value) ? theme.blueLight : theme.white,
                        color: String(val) === String(p.best_value) ? theme.blueText : theme.text3,
                        border: `1px solid ${theme.border}`,
                      }}>
                        {val}<span style={{ opacity: 0.6 }}> ×{count}</span>
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 10, color: theme.text4 }}>
                    n={p.n_runs} runs · dominant share: {p.dominant_share_pct}%
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}


// ══════════════════════════════════════════════════════
// History Panel — backups + rollback
// ══════════════════════════════════════════════════════
function HistoryPanel({ history, currentVersion, onRollback }) {
  if (!history) {
    return (
      <Card>
        <CardHeader title="📚 Versions-Historie"/>
        <div style={{ padding: 20, fontSize: 12, color: theme.text3, textAlign: "center" }}>Lade…</div>
      </Card>
    );
  }
  const backups = history.backups || [];

  return (
    <Card>
      <CardHeader title="📚 Versions-Historie" sub={`${backups.length} Backup(s)`}/>
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {/* Current HEAD */}
        <div style={{
          padding: "10px 16px", borderBottom: `1px solid ${theme.divider}`,
          background: theme.greenLight2,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12 }}>🚀</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: theme.greenText, fontFamily: fonts.mono }}>
              HEAD · v{currentVersion}
            </span>
            <span style={{
              fontSize: 9, padding: "1px 7px", borderRadius: 4,
              background: theme.green, color: "#fff", fontWeight: 700,
            }}>LIVE</span>
          </div>
          <div style={{ fontSize: 10, color: theme.text3, marginTop: 2, marginLeft: 20 }}>
            aktuelle Haupt-Strategie
          </div>
        </div>
        {/* Backups */}
        {backups.map(b => (
          <div key={b.filename} style={{
            padding: "10px 16px", borderBottom: `1px solid ${theme.divider}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11 }}>📦</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: theme.text, fontFamily: fonts.mono }}>
                v{b.version}
              </span>
              <span style={{ fontSize: 10, color: theme.text4 }}>·</span>
              <span style={{ fontSize: 10, color: theme.text3 }}>{fmtDate(b.backed_up_at)}</span>
              <div style={{ flex: 1 }}/>
              <button onClick={() => onRollback(b.filename)}
                      style={{
                        padding: "3px 10px", fontSize: 10, borderRadius: 4,
                        border: `1px solid ${theme.border}`, background: theme.white,
                        color: theme.text3, cursor: "pointer",
                      }}
                      title="Zu dieser Version zurückrollen">
                ⏪ Rollback
              </button>
            </div>
            <div style={{ fontSize: 9, color: theme.text4, marginTop: 2, marginLeft: 18, fontFamily: fonts.mono }}>
              {b.filename}
            </div>
          </div>
        ))}
        {backups.length === 0 && (
          <div style={{ padding: 20, fontSize: 11, color: theme.text3, textAlign: "center" }}>
            Keine Backups. Wird automatisch bei jedem Promote erstellt.
          </div>
        )}
      </div>
    </Card>
  );
}


// ══════════════════════════════════════════════════════
// Experiments Comparison Table
// ══════════════════════════════════════════════════════
function ExperimentsTable({ experiments, baseRuns, selectedExp, onSelectExp }) {
  return (
    <Card>
      <CardHeader title="🧪 Experimente — Vergleich"
                  sub={`${experiments.length} Variante(n) · ${baseRuns?.optimizations?.length || 0} Base-Opt · ${baseRuns?.walkforwards?.length || 0} Base-WF`}/>
      <div style={{ padding: "4px 0" }}>
        {experiments.length === 0 && (
          <div style={{ padding: 20, fontSize: 12, color: theme.text3, textAlign: "center" }}>
            Noch keine Experimente. Erstelle eins im Analyse-Tab über 🧪 Experiment erstellen.
          </div>
        )}
        {experiments.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: fonts.body,
            }}>
              <thead>
                <tr style={{ background: theme.tint, color: theme.text3 }}>
                  <th style={th}>Name</th>
                  <th style={th}>Abgeleitet von</th>
                  <th style={th}>Änderungen</th>
                  <th style={{ ...th, textAlign: "right" }}>Best Opt</th>
                  <th style={{ ...th, textAlign: "center" }}>#Opt</th>
                  <th style={{ ...th, textAlign: "right" }}>Latest WF</th>
                  <th style={{ ...th, textAlign: "center" }}>#WF</th>
                  <th style={{ ...th, textAlign: "center" }}>Verdict</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {experiments.map(exp => {
                  const selected = selectedExp === exp.filename;
                  const vStyle = verdictStyle(exp.best_wf_verdict);
                  return (
                    <tr key={exp.filename} onClick={() => onSelectExp(selected ? null : exp.filename)}
                        style={{
                          background: selected ? theme.blueLight : "transparent",
                          cursor: "pointer",
                          borderBottom: `1px solid ${theme.divider}`,
                        }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: theme.text, fontFamily: fonts.mono, fontSize: 11 }}>
                          🧪 {exp.experiment_name}
                        </div>
                        {exp.note && (
                          <div style={{ fontSize: 9, color: theme.text4, fontStyle: "italic", marginTop: 2 }}>
                            {exp.note.substring(0, 60)}{exp.note.length > 60 ? "…" : ""}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: fonts.mono, fontSize: 10, color: theme.text3 }}>
                        {exp.derived_from_run || "—"}
                      </td>
                      <td style={td}>
                        <span style={{
                          fontSize: 10, padding: "1px 7px", borderRadius: 10,
                          background: theme.tint, color: theme.text2,
                        }}>
                          {(exp.changes || []).length} Feld(er)
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: fonts.mono }}>
                        <span style={{
                          color: exp.best_opt_pnl > 0 ? theme.greenText : (exp.best_opt_pnl < 0 ? theme.redText : theme.text3),
                          fontWeight: 600,
                        }}>
                          {fmtUsd(exp.best_opt_pnl)}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "center", color: theme.text3 }}>{exp.n_opt_runs || 0}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: fonts.mono }}>
                        <span style={{
                          color: exp.latest_wf_oos_pnl > 0 ? theme.greenText : (exp.latest_wf_oos_pnl < 0 ? theme.redText : theme.text3),
                        }}>
                          {fmtUsd(exp.latest_wf_oos_pnl)}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "center", color: theme.text3 }}>{exp.n_wf_runs || 0}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        {exp.best_wf_verdict && (
                          <span style={{
                            fontSize: 9, padding: "2px 8px", borderRadius: 10,
                            background: vStyle.bg, color: vStyle.color, fontWeight: 700,
                          }}>
                            {vStyle.label}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontSize: 10, color: theme.text4 }}>
                        {selected ? "▼" : "▶"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

const th = {
  padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 10,
  textTransform: "uppercase", letterSpacing: 0.5,
  borderBottom: `1px solid ${theme.border}`,
};
const td = { padding: "10px 12px", verticalAlign: "middle" };


// ══════════════════════════════════════════════════════
// Experiment Detail Panel
// ══════════════════════════════════════════════════════
function ExperimentDetailPanel({ expFilename, diff, preflight, onClose, onPromote }) {
  return (
    <Card>
      <CardHeader title={`🔍 Detail: ${expFilename.replace(/^experiments\//, "")}`}
                  sub="Diff gegen Base + Pre-Flight-Check"
                  right={<button onClick={onClose}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: theme.text3, fontSize: 16 }}>✕</button>}/>
      <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* LEFT: DIFF */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.text2, marginBottom: 8 }}>
            📝 Änderungen gegenüber Base {diff && `(${diff.n_differences})`}
          </div>
          {!diff && <div style={{ fontSize: 11, color: theme.text3 }}>Lade Diff…</div>}
          {diff && diff.diffs.length === 0 && (
            <div style={{ fontSize: 11, color: theme.text3 }}>Keine Unterschiede gefunden.</div>
          )}
          {diff && diff.diffs.map((d, i) => (
            <div key={i} style={{
              padding: "6px 10px", marginBottom: 4, borderRadius: 4,
              background: theme.tint, fontFamily: fonts.mono, fontSize: 10,
            }}>
              <div style={{ color: theme.text2, fontWeight: 600 }}>{d.path}</div>
              <div style={{ marginTop: 2, display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{
                  color: theme.redText, padding: "1px 6px", borderRadius: 3,
                  background: theme.redLight, fontSize: 9,
                }}>base: {JSON.stringify(d.base_value)}</span>
                <span style={{ color: theme.text4 }}>→</span>
                <span style={{
                  color: theme.greenText, padding: "1px 6px", borderRadius: 3,
                  background: theme.greenLight2, fontSize: 9,
                }}>exp: {JSON.stringify(d.experiment_value)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: PREFLIGHT */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.text2, marginBottom: 8 }}>
            ✅ Pre-Flight Check
          </div>
          {!preflight && <div style={{ fontSize: 11, color: theme.text3 }}>Lade…</div>}
          {preflight && preflight.checks.map((c, i) => {
            const icon = c.ok ? "✅" : (c.level === "error" ? "❌" : "⚠️");
            const color = c.ok ? theme.greenText : (c.level === "error" ? theme.redText : "#854d0e");
            return (
              <div key={i} style={{
                padding: "6px 10px", marginBottom: 4, borderRadius: 4,
                background: theme.white, border: `1px solid ${theme.divider}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span>{icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color }}>{c.check}</span>
                </div>
                <div style={{ fontSize: 10, color: theme.text3, marginLeft: 22, marginTop: 2 }}>
                  {c.detail}
                </div>
              </div>
            );
          })}
          {preflight && (
            <div style={{
              marginTop: 10, padding: "10px 12px", borderRadius: 6,
              background: preflight.can_promote ? theme.greenLight2 : theme.redLight,
              fontSize: 11, fontWeight: 600,
              color: preflight.can_promote ? theme.greenText : theme.redText,
            }}>
              {preflight.can_promote ? "✅ Bereit für Promote" : "❌ Pre-Flight nicht bestanden"}
              {preflight.has_warnings && preflight.can_promote && <span> (mit Warnungen)</span>}
            </div>
          )}
        </div>
      </div>

      {/* PROMOTE BUTTON */}
      <div style={{
        padding: "10px 16px", borderTop: `1px solid ${theme.divider}`,
        display: "flex", justifyContent: "flex-end", gap: 8,
      }}>
        <Button primary onClick={onPromote}
                disabled={!preflight}
                style={{ padding: "8px 18px", fontSize: 12 }}>
          🚀 Promote zu Haupt-Strategie…
        </Button>
      </div>
    </Card>
  );
}


// ══════════════════════════════════════════════════════
// Promote Modal
// ══════════════════════════════════════════════════════
function PromoteModal({ expFilename, preflight, versionBump, setVersionBump, note, setNote,
                        force, setForce, promoting, onCancel, onConfirm }) {
  const needsForce = preflight && !preflight.can_promote;
  const hasStructural = preflight && preflight.n_structural_changes > 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,41,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.white, borderRadius: 12, maxWidth: 560, width: "100%",
        maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${theme.divider}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
            🚀 Experiment promoten
          </div>
          <div style={{ fontSize: 11, color: theme.text3, marginTop: 4 }}>
            Experiment <code style={{ background: theme.grayLight, padding: "1px 6px", borderRadius: 3, fontFamily: fonts.mono }}>
              {expFilename}</code> wird zur neuen Haupt-Strategie.
          </div>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Version bump */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, display: "block", marginBottom: 4 }}>
              Version-Bump
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { val: "auto", label: "Automatisch (empfohlen)" },
                { val: "patch", label: "Patch (+0.0.1)" },
                { val: "minor", label: "Minor (+0.1.0)" },
              ].map(opt => (
                <button key={opt.val} onClick={() => setVersionBump(opt.val)}
                        style={{
                          padding: "6px 12px", fontSize: 11, borderRadius: 6,
                          border: versionBump === opt.val ? `2px solid ${theme.blue}` : `1px solid ${theme.border}`,
                          background: versionBump === opt.val ? theme.blueLight : theme.white,
                          color: versionBump === opt.val ? theme.blueText : theme.text2,
                          cursor: "pointer", fontWeight: 600,
                        }}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: theme.text4, marginTop: 4 }}>
              Auto wählt Minor bei strukturellen Änderungen, sonst Patch.
            </div>
          </div>

          {/* Note */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, display: "block", marginBottom: 4 }}>
              Notiz (wird in Changelog + History gespeichert)
            </label>
            <input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="z.B. Score-Relaxation Test nach WF#17 SOLID verdict"
                   style={{
                     width: "100%", padding: "8px 12px", fontSize: 12,
                     border: `1px solid ${theme.border}`, borderRadius: 6,
                     background: theme.white, color: theme.text,
                   }}/>
          </div>

          {/* Warnings */}
          {hasStructural && (
            <div style={{
              padding: 12, borderRadius: 8,
              background: "#fed7aa", border: `1px solid #fb923c`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9a3412", marginBottom: 4 }}>
                ⚠️ Strukturelle Änderungen ({preflight.n_structural_changes})
              </div>
              <div style={{ fontSize: 11, color: "#7c2d12", lineHeight: 1.5 }}>
                Dieses Experiment ändert strukturelle Pfade ({preflight.structural_paths.slice(0, 3).join(", ")}…).
                Die Haupt-Strategie ändert damit ihre Identität. Alte WF-Ergebnisse sind nicht mehr direkt vergleichbar.
                Trotzdem promoten?
              </div>
            </div>
          )}

          {needsForce && (
            <div style={{
              padding: 12, borderRadius: 8, background: theme.redLight, border: `1px solid ${theme.redText}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.redText, marginBottom: 6 }}>
                ❌ Pre-Flight fehlgeschlagen
              </div>
              <label style={{ fontSize: 11, color: theme.redText, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)}/>
                Ja, ich will trotzdem promoten (force=true)
              </label>
            </div>
          )}
        </div>

        <div style={{
          padding: "14px 22px", borderTop: `1px solid ${theme.divider}`,
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <Button onClick={onCancel} style={{ padding: "8px 16px", fontSize: 12 }}>
            Abbrechen
          </Button>
          <Button primary
                  disabled={promoting || (needsForce && !force)}
                  onClick={onConfirm}
                  style={{ padding: "8px 20px", fontSize: 12 }}>
            {promoting ? "Promote…" : "🚀 Jetzt promoten"}
          </Button>
        </div>
      </div>
    </div>
  );
}
